import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

import baseTheme from 'theme'
import media from 'media'

import Markdown from '@anvilco-pkg/ui/components/Markdown'

import { calculateLength } from 'helpers'
import {
  DEFAULT_MOBILE_BODY_FONT_SIZE_STRING,
  DEFAULT_BODY_FONT_SIZE_STRING,
} from 'consts'

import QuoteAsset from './assets/CardQuote'

export const MOBILE_PADDING_X = 40
export const PADDING_X = 77

export const StyledQuote = styled(QuoteAsset)`
  position: absolute;
  top: 0;
  left: 0;
  width: 34px;
  height: fit-content;

  ${media.desktop`
      width: 62px;
  `}
`

const getContainerAttrs = ({ isVertical }) => ({
  flexDirection: isVertical ? 'column' : ['column', 'column', 'row'],
  justifyContent: ['flex-start', 'flex-start', 'center'],
})

export const Container = styled(Flex)
  .attrs(getContainerAttrs)
  .withConfig({
    shouldForwardProp: (prop) =>
      ['isVertical', 'withQuoteMarks'].indexOf(prop) === -1,
  })`
  position: relative; 
  ${media.desktop`
    gap: ${({ isVertical }) => (isVertical ? '40px' : '70px')};
  `}

  ${({ withQuoteMarks }) =>
    withQuoteMarks &&
    `
      padding-left: ${MOBILE_PADDING_X}px;
  `}

  ${({ withQuoteMarks }) =>
    withQuoteMarks &&
    media.desktop`
      padding-left: ${PADDING_X}px;
      padding-top: 15px;
  `}
`

export const Quote = styled.div.withConfig({
  shouldForwardProp: (prop) => ['isVertical'].indexOf(prop) === -1,
})`
  font-style: italic;
  font-size: ${({ theme }) => {
    return calculateLength(
      theme?.fontSizes?.mobile?.body || DEFAULT_MOBILE_BODY_FONT_SIZE_STRING,
      '+',
      2
    )
  }};

  ${media.desktop`
    width: ${({ isVertical }) => (isVertical ? '100%' : '50%')};
    font-size: ${({ theme }) => {
      return calculateLength(
        theme?.fontSizes?.body || DEFAULT_BODY_FONT_SIZE_STRING,
        '+',
        4
      )
    }};
  `};

  ${media.giant`
    font-size: ${({ theme }) => {
      return calculateLength(
        theme?.fontSizes?.body || DEFAULT_BODY_FONT_SIZE_STRING,
        '+',
        6
      )
    }};
  `};
`

const getAuthorContainerAttrs = ({ isVertical }) => ({
  flexDirection: isVertical ? ['row'] : ['column'],
  alignItems: isVertical ? 'center' : 'flex-start',
  mt: isVertical ? [20] : [0],
})
const AuthorContainer = styled(Flex)
  .attrs(getAuthorContainerAttrs)
  .withConfig({
    shouldForwardProp: (prop) => ['isVertical'].indexOf(prop) === -1,
  })`
  width: fit-content;
  /* ${({ isVertical }) => isVertical && 'margin: 0 auto'}; */
`

export const HeadshotContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => ['isVertical'].indexOf(prop) === -1,
})`
  ${({ isVertical }) => !isVertical && 'display: none;'};
  width: ${({ isVertical }) => (isVertical ? '55px' : '90px')};

  ${media.desktop`
    display: block;
  `}
`

const getAuthorTextAttrs = ({ isVertical }) => ({
  ml: isVertical ? [12] : [0],
})
const AuthorText = styled(Box)
  .attrs(getAuthorTextAttrs)
  .withConfig({
    shouldForwardProp: (prop) => ['isVertical'].indexOf(prop) === -1,
  })``

const getAuthorAttrs = ({ theme, isVertical }) => {
  const mobile = calculateLength(
    theme?.fontSizes?.mobile?.body || '16px',
    '+',
    2
  )
  const desktop = calculateLength(theme?.fontSizes?.body || '16px', '+', 2)

  return {
    fontSize: [mobile, mobile, mobile, desktop],
    mt: isVertical ? 0 : [24, 24, 24, 8],
  }
}
const Author = styled(Box)
  .attrs(getAuthorAttrs)
  .withConfig({
    shouldForwardProp: (prop) => ['isVertical'].indexOf(prop) === -1,
  })`
  color: #0298bf;
  font-weight: 600;
`

const getTitleAttrs = ({ theme }) => {
  const mobile = calculateLength(
    theme?.fontSizes?.mobile?.body || '16px',
    '-',
    1
  )
  const desktop = calculateLength(theme?.fontSizes?.body || '16px', '-', 2)

  return {
    fontSize: [mobile, mobile, mobile, desktop],
    mt: [4],
  }
}
const Title = styled(Box).attrs(getTitleAttrs)`
  margin-top: 4px;
`

// TODO: move this to a more shared location and clean up for use errrrywhere
const getImageFromData = (image, title) => {
  if (!image) {
    return null
  }
  return image?.gatsbyImageData ? (
    <GatsbyImage image={getImage(image?.gatsbyImageData)} alt={title} />
  ) : (
    <img src={image} alt={title} />
  )
}

export default function InlineTestimonial ({
  sanityQuoteTitle,
  quote,
  quoteUsesMarkdown,
  author,
  authorHeadshot,
  title,
  isVertical,
  withQuoteMarks,
  quoteColor,
  ...others
}) {
  const quoteText =
    // typeof check is for sanity generated elements
    withQuoteMarks || typeof quote !== 'string' ? quote : <>"{quote}"</>

  return (
    <Container
      {...others}
      isVertical={isVertical}
      withQuoteMarks={withQuoteMarks}
    >
      {withQuoteMarks && <StyledQuote fill={quoteColor} />}
      <Quote isVertical={isVertical} as={quoteUsesMarkdown ? Markdown : null}>
        {quoteText}
      </Quote>
      <AuthorContainer isVertical={isVertical}>
        <HeadshotContainer isVertical={isVertical}>
          {getImageFromData(authorHeadshot, 'author headshot')}
        </HeadshotContainer>
        <AuthorText isVertical={isVertical}>
          <Author isVertical={isVertical}>{author}</Author>
          <Title>{title}</Title>
        </AuthorText>
      </AuthorContainer>
    </Container>
  )
}

InlineTestimonial.propTypes = {
  quote: PropTypes.string.isRequired,
  author: PropTypes.string.isRequired,
  authorHeadshot: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  isVertical: PropTypes.bool,
  withQuoteMarks: PropTypes.bool,
  quoteColor: PropTypes.string,
}

InlineTestimonial.defaultProps = {
  quoteColor: baseTheme.colors.blues[20],
}
